import { getPricingForCompositionExtra } from '@koob/utils';

type Composition =
  | { adults?: number; childrenBirthdates?: unknown[] }
  | null
  | undefined;

interface EnrichParams {
  selectedExtras: any;
  dayIndex: number;
  experience: any;
  priceObject: any;
  tripComposition: Composition;
  // Keys of the extras already handled: an extra is enriched (or dropped) once,
  // so an unavailable one is reported once. The caller keeps it across renders,
  // and this function adds to it.
  processedKeys: Set<string>;
}

// KD-3650 — each occurrence is priced on its own pax count, with the trip
// composition as the fallback for the lines saved before the ticket. The
// composition is taken as a whole, never field by field: `adultCount` is what
// says the line owns a pax count, and its children go with it. Mixing the
// adults saved on the line with every child of the trip builds a party nobody
// ever selected, and no pricing grid answers it.
function getOccurrenceComposition(extra: any, tripComposition: Composition) {
  return extra.adultCount != null
    ? {
        adults: extra.adultCount,
        childrenBirthdates: extra.childrenBirthdates ?? [],
      }
    : {
        adults: tripComposition?.adults ?? 0,
        childrenBirthdates: tripComposition?.childrenBirthdates ?? [],
      };
}

// The occurrence key is part of the identity: without it two occurrences of
// the same service on the same day share one key, the first one marks it as
// processed and the next ones are skipped, so they stay without price nor
// `perPax` after a template is opened.
function getExtraKey(extra: any): string {
  const parentIdPart = extra.parentExtraId ? `-${extra.parentExtraId}` : '';

  return `${extra.id}-${extra.dayIndex}-${extra.experienceId}${parentIdPart}-${extra.occurrenceKey ?? 'single'}`;
}

function priceExtra(extra: any, optionData: any, params: EnrichParams) {
  const { experience, priceObject, tripComposition } = params;
  const composition = getOccurrenceComposition(extra, tripComposition);
  const pricing = getPricingForCompositionExtra({
    composition,
    pricings: optionData?.pricings,
  });

  const participants =
    pricing?.validForAdultCount + pricing?.validForChildrenBirthdates?.length;
  const perPaxShare = (amount: number) =>
    pricing?.perPax ? amount / participants : amount;

  return {
    ...extra,
    name: optionData?.name ?? extra.name,
    adultCount: composition.adults,
    childrenBirthdates: composition.childrenBirthdates,
    bestMatches: experience?.bestMatches ?? [],
    incompatibilities: experience?.incompatibilities ?? [],
    price: perPaxShare(pricing?.price),
    priceWithoutPromo: perPaxShare(pricing?.priceWithoutPromo),
    perPax: pricing?.perPax ?? extra?.perPax ?? false,
    currency: priceObject?.currency,
  };
}

// Backfills the pricing of the extras a trip was loaded with (a template, a
// saved version) that still lack it, from the program's fresh price. An extra
// the price no longer offers is dropped and reported through
// `someUnavailable`.
export function enrichSelectedExtras(params: EnrichParams) {
  const { selectedExtras, dayIndex, experience, priceObject, processedKeys } =
    params;
  const currentExtras: any[] = Array.isArray(selectedExtras)
    ? selectedExtras
    : [];
  let someUnavailable = false;
  let hasChanges = false;

  const extras = currentExtras.map((extra) => {
    const extraKey = getExtraKey(extra);

    // Skip if it doesn't match this experience/day, was already handled, or
    // already has pricing AND perPax
    if (
      extra.experienceId !== experience?.id ||
      extra.dayIndex !== dayIndex ||
      processedKeys.has(extraKey) ||
      (extra.price !== undefined && extra.perPax !== undefined)
    ) {
      return extra;
    }

    hasChanges = true;
    // Marked as processed even when unavailable, to avoid repeat notifications
    processedKeys.add(extraKey);

    const optionData = priceObject?.extras?.find(
      (e: any) => e?.id === extra.id,
    );

    if (!optionData || optionData?.pricings?.length === 0) {
      someUnavailable = true;
      return null;
    }

    return priceExtra(extra, optionData, params);
  });

  return {
    extras: extras.filter(Boolean),
    hasChanges,
    someUnavailable,
  };
}
